// =============================================================================
// TOKENS DE BORDES Y RADIOS - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Tokens de bordes centralizados para crear consistencia visual
// en todos los componentes del sistema

// Anchos de borde base
export const borderWidths = {
  0: '0',
  1: '1px',
  2: '2px',
  4: '4px',
  8: '8px',
  
  // Alias semánticos
  none: '0',
  thin: '1px',
  medium: '2px',
  thick: '4px',
  'extra-thick': '8px',
};

// Estilos de borde
export const borderStyles = {
  none: 'none',
  solid: 'solid',
  dashed: 'dashed',
  dotted: 'dotted',
  double: 'double',
  groove: 'groove',
  ridge: 'ridge',
  inset: 'inset',
  outset: 'outset',
};

// Radios de borde (border-radius)
export const borderRadius = {
  // Valores base
  none: '0',
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  '2xl': '16px',
  '3xl': '24px',
  
  // Valores especiales
  full: '9999px', // Para elementos completamente redondeados
  
  // Alias semánticos
  button: '6px',
  input: '6px',
  card: '8px',
  modal: '12px',
  badge: '9999px',
  avatar: '9999px',
};

// Colores de borde para tema claro
export const borderColors = {
  // Grises neutros
  transparent: 'transparent',
  current: 'currentColor',
  
  // Grises del sistema
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
  
  // Colores de estado (Yool v5.2 — var equivalentes: --yool-success, --yool-error, --yool-warn, --fy-cyan)
  success: '#158B5C',
  error: '#ED4068',
  warning: '#F59E0B',
  info: '#06B6D4',
  
  // Colores primarios
  primary: '#3b82f6',
  secondary: '#6366f1',
  
  // Alias semánticos
  default: '#e5e7eb',
  muted: '#f3f4f6',
  subtle: '#f9fafb',
  strong: '#d1d5db',
  emphasis: '#9ca3af',
};

// Colores de borde para tema oscuro
export const darkBorderColors = {
  // Grises para tema oscuro
  gray: {
    50: '#1f2937',
    100: '#374151',
    200: '#4b5563',
    300: '#6b7280',
    400: '#9ca3af',
    500: '#d1d5db',
    600: '#e5e7eb',
    700: '#f3f4f6',
    800: '#f9fafb',
    900: '#ffffff',
  },
  
  // Colores de estado para tema oscuro
  success: '#059669',
  error: '#dc2626',
  warning: '#d97706',
  info: '#2563eb',
  
  // Colores primarios para tema oscuro
  primary: '#60a5fa',
  secondary: '#818cf8',
  
  // Alias semánticos para tema oscuro
  default: '#4b5563',
  muted: '#374151',
  subtle: '#1f2937',
  strong: '#6b7280',
  emphasis: '#9ca3af',
};

// Bordes específicos para componentes
export const componentBorders = {
  // Botones
  button: {
    primary: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: 'transparent',
      radius: borderRadius.button,
    },
    secondary: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.default,
      radius: borderRadius.button,
    },
    outline: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.primary,
      radius: borderRadius.button,
    },
    ghost: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: 'transparent',
      radius: borderRadius.button,
    },
  },
  
  // Inputs y formularios
  input: {
    default: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.default,
      radius: borderRadius.input,
    },
    focus: {
      width: borderWidths[2],
      style: borderStyles.solid,
      color: borderColors.primary,
      radius: borderRadius.input,
    },
    error: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.error,
      radius: borderRadius.input,
    },
    success: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.success,
      radius: borderRadius.input,
    },
  },
  
  // Cards
  card: {
    default: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.muted,
      radius: borderRadius.card,
    },
    elevated: {
      width: borderWidths.none,
      style: borderStyles.none,
      color: 'transparent',
      radius: borderRadius.card,
    },
    interactive: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.default,
      radius: borderRadius.card,
    },
  },
  
  // Modales
  modal: {
    default: {
      width: borderWidths.none,
      style: borderStyles.none,
      color: 'transparent',
      radius: borderRadius.modal,
    },
  },
  
  // Tablas
  table: {
    cell: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.muted,
      radius: borderRadius.none,
    },
    header: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.default,
      radius: borderRadius.none,
    },
  },
  
  // Navegación
  nav: {
    item: {
      width: borderWidths.none,
      style: borderStyles.none,
      color: 'transparent',
      radius: borderRadius.md,
    },
    active: {
      width: borderWidths[2],
      style: borderStyles.solid,
      color: borderColors.primary,
      radius: borderRadius.md,
    },
  },
  
  // Badges y chips
  badge: {
    default: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.default,
      radius: borderRadius.badge,
    },
    outline: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.primary,
      radius: borderRadius.badge,
    },
  },
  
  // Avatares
  avatar: {
    default: {
      width: borderWidths[2],
      style: borderStyles.solid,
      color: borderColors.muted,
      radius: borderRadius.avatar,
    },
    online: {
      width: borderWidths[2],
      style: borderStyles.solid,
      color: borderColors.success,
      radius: borderRadius.avatar,
    },
  },
  
  // Divisores
  divider: {
    horizontal: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.muted,
      radius: borderRadius.none,
    },
    vertical: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.muted,
      radius: borderRadius.none,
    },
  },
};

// Bordes específicos para el sistema de pagos
export const paymentBorders = {
  // Cards de método de pago
  paymentCard: {
    default: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.default,
      radius: borderRadius.lg,
    },
    selected: {
      width: borderWidths[2],
      style: borderStyles.solid,
      color: borderColors.primary,
      radius: borderRadius.lg,
    },
    error: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.error,
      radius: borderRadius.lg,
    },
  },
  
  // Formularios de pago
  paymentForm: {
    container: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.muted,
      radius: borderRadius.xl,
    },
    field: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.default,
      radius: borderRadius.input,
    },
    fieldFocus: {
      width: borderWidths[2],
      style: borderStyles.solid,
      color: borderColors.primary,
      radius: borderRadius.input,
    },
    fieldError: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.error,
      radius: borderRadius.input,
    },
  },
  
  // Estados de transacción
  transaction: {
    pending: {
      width: borderWidths[1],
      style: borderStyles.dashed,
      color: borderColors.warning,
      radius: borderRadius.md,
    },
    success: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.success,
      radius: borderRadius.md,
    },
    failed: {
      width: borderWidths[1],
      style: borderStyles.solid,
      color: borderColors.error,
      radius: borderRadius.md,
    },
  },
};

// Utilidades para crear bordes
export const borderUtilities = {
  // Crear borde completo
  createBorder: (width = '1px', style = 'solid', color = borderColors.default) => {
    return `${width} ${style} ${color}`;
  },
  
  // Crear solo borde superior
  createBorderTop: (width = '1px', style = 'solid', color = borderColors.default) => {
    return {
      borderTop: `${width} ${style} ${color}`,
    };
  },
  
  // Crear solo borde inferior
  createBorderBottom: (width = '1px', style = 'solid', color = borderColors.default) => {
    return {
      borderBottom: `${width} ${style} ${color}`,
    };
  },
  
  // Crear solo borde izquierdo
  createBorderLeft: (width = '1px', style = 'solid', color = borderColors.default) => {
    return {
      borderLeft: `${width} ${style} ${color}`,
    };
  },
  
  // Crear solo borde derecho
  createBorderRight: (width = '1px', style = 'solid', color = borderColors.default) => {
    return {
      borderRight: `${width} ${style} ${color}`,
    };
  },
  
  // Crear radio personalizado
  createRadius: (topLeft, topRight, bottomRight, bottomLeft) => {
    if (topRight === undefined) {
      return { borderRadius: topLeft };
    }
    return {
      borderRadius: `${topLeft} ${topRight} ${bottomRight || topRight} ${bottomLeft || topLeft}`,
    };
  },
  
  // Crear borde responsivo
  createResponsiveBorder: (mobile, desktop) => {
    return {
      border: mobile,
      '@media (min-width: 768px)': {
        border: desktop,
      },
    };
  },
  
  // Crear borde con hover
  createHoverBorder: (defaultBorder, hoverBorder) => {
    return {
      border: defaultBorder,
      '&:hover': {
        border: hoverBorder,
      },
    };
  },
};

// Patrones de borde comunes
export const borderPatterns = {
  // Borde de enfoque accesible
  focusRing: {
    outline: 'none',
    boxShadow: `0 0 0 2px ${borderColors.primary}`,
  },
  
  // Borde de error
  errorBorder: {
    border: borderUtilities.createBorder(borderWidths[1], borderStyles.solid, borderColors.error),
    boxShadow: `0 0 0 1px ${borderColors.error}`,
  },
  
  // Borde de éxito
  successBorder: {
    border: borderUtilities.createBorder(borderWidths[1], borderStyles.solid, borderColors.success),
    boxShadow: `0 0 0 1px ${borderColors.success}`,
  },
  
  // Borde de advertencia
  warningBorder: {
    border: borderUtilities.createBorder(borderWidths[1], borderStyles.solid, borderColors.warning),
    boxShadow: `0 0 0 1px ${borderColors.warning}`,
  },
  
  // Borde de información
  infoBorder: {
    border: borderUtilities.createBorder(borderWidths[1], borderStyles.solid, borderColors.info),
    boxShadow: `0 0 0 1px ${borderColors.info}`,
  },
};

// Animaciones de borde
export const borderAnimations = {
  // Transiciones suaves
  transition: {
    fast: 'border-color 0.15s ease-in-out, border-width 0.15s ease-in-out',
    normal: 'border-color 0.2s ease-in-out, border-width 0.2s ease-in-out',
    slow: 'border-color 0.3s ease-in-out, border-width 0.3s ease-in-out',
  },
  
  // Efectos hover
  hover: {
    colorChange: {
      transition: 'border-color 0.2s ease-in-out',
    },
    widthChange: {
      transition: 'border-width 0.2s ease-in-out',
    },
  },
};

// Exportar todo
export default {
  widths: borderWidths,
  styles: borderStyles,
  radius: borderRadius,
  colors: borderColors,
  darkColors: darkBorderColors,
  component: componentBorders,
  payment: paymentBorders,
  utilities: borderUtilities,
  patterns: borderPatterns,
  animations: borderAnimations,
};